import { useState, type FC } from "react";

import { Card, CardContent } from "@/shared/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
} from "@/shared/components/ui/dialog";

import { glossaryTerms, type GlossaryTerm } from "@/shared/constants/glossaryTerms";

interface GlossaryCardProps {
  term: GlossaryTerm;
  onClick: () => void;
}

const GlossaryCard: FC<GlossaryCardProps> = ({ term, onClick }) => {
  return (
    <Card
      className="border-primary bg-white rounded-xl shadow-none cursor-pointer transition-colors hover:bg-subtle"
      onClick={onClick}
    >
      <CardContent className="flex gap-4 p-5">
        <img
          src={term.image}
          alt={term.title}
          className="h-16 w-16 shrink-0 rounded-lg object-contain"
        />
        <div className="min-w-0">
          <h4 className="text-base font-medium">{term.title}</h4>
          <p className="text-secondary text-sm mt-1 leading-relaxed line-clamp-3">{term.summary}</p>
        </div>
      </CardContent>
    </Card>
  );
};

export const Glossary: FC = () => {
  const [selectedTerm, setSelectedTerm] = useState<GlossaryTerm | null>(null);

  return (
    <div>
      <p className="text-secondary mb-6">
        Estate settlement comes with a lot of unfamiliar language, and that's completely normal.
        This glossary is here to help you feel confident, as you move forward at your own pace.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {glossaryTerms.map((term) => (
          <GlossaryCard key={term.id} term={term} onClick={() => setSelectedTerm(term)} />
        ))}
      </div>

      <Dialog
        open={!!selectedTerm}
        onOpenChange={(open) => {
          if (!open) setSelectedTerm(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            {selectedTerm && (
              <div className="flex items-center gap-3">
                <img
                  src={selectedTerm.image}
                  alt={selectedTerm.title}
                  className="h-12 w-12 shrink-0 rounded-lg object-contain"
                />
                <DialogTitle>{selectedTerm.title}</DialogTitle>
              </div>
            )}
          </DialogHeader>
          <DialogBody>
            {selectedTerm && (
              <div className="flex flex-col gap-4">
                {selectedTerm.definition.map((paragraph, i) => (
                  <p key={i} className="text-secondary text-sm leading-relaxed">
                    {paragraph}
                  </p>
                ))}
              </div>
            )}
          </DialogBody>
        </DialogContent>
      </Dialog>
    </div>
  );
};
